import axios from 'axios';
import AddButton from 'components/AddButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import UpsertLabTestPanelForm from 'components/settings/lab-test-panel/UpsertLabTestPanelForm';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Dimmer, Grid, Loader, Modal } from 'semantic-ui-react';
import { getAllLabTestComponents } from 'services/labTestComponentService';
import { deleteLabTestPanel, getAllLabTestPanels, getLabTestPanel } from 'services/labTestPanelService';
import { LabTestComponentDto, LabTestPanelDto, UpsertLabTestPanelDto } from 'ts-types/api.types';

const cancelTokenSource = axios.CancelToken.source();

interface Props extends MessageSourceProps {
}

const LabTestPanelView = (props: Props) => {

  const {getMessage} = props;

  const [labTestPanels, setLabTestPanels] = useState<LabTestPanelDto[]>([]);
  const [labTestComponents, setLabTestComponents] = useState<{ [key: number]: LabTestComponentDto }>({});
  const [labTestPanel, setLabTestPanel] = useState<UpsertLabTestPanelDto>();
  const [labTestPanelId, setLabTestPanelId] = useState<number>();
  const [addEditModalOpen, setAddEditModalOpen] = useState<boolean>(false);
  const [labTestPanelsLoaded, setLabTestPanelsLoaded] = useState<boolean>(false);


  useEffect(() => {
    fetchLabTestPanels();
  }, []);

  const fetchLabTestPanels = async () => {

    const labTestComponents = await getAllLabTestComponents(cancelTokenSource);
    const componentMap: {[key:number]: LabTestComponentDto} = _.keyBy(labTestComponents, "id");
    setLabTestComponents(componentMap);

    const labTestPanels = await getAllLabTestPanels(cancelTokenSource);
    setLabTestPanels(labTestPanels);

    setLabTestPanelsLoaded(true);
  }

  const openAddEditModal = async (id: number | undefined) => {
    if (id) {
      const labTestPanel = await getLabTestPanel(id, cancelTokenSource);

      setLabTestPanel(labTestPanel);
      setLabTestPanelId(id);
    }

    setAddEditModalOpen(true);
  }

  const onSaveUpsertForm = async () => {
    const labTestPanels = await getAllLabTestPanels(cancelTokenSource);
    setLabTestPanels(labTestPanels);
    setAddEditModalOpen(false);
  }

  const hideModal = () => {
    setLabTestPanelId(undefined);
    setLabTestPanel(undefined);
    setAddEditModalOpen(false);
  }

  const labTestPanelDelete = async (id: number) => {
    const filteredLabPanels = labTestPanels.filter(labTestPanel => labTestPanel.id != id);
    await deleteLabTestPanel(id, cancelTokenSource)
        .then(() => setLabTestPanels(filteredLabPanels));
  }

  const renderLoader = (): React.ReactNode => {

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">{getMessage("labTestComponent.loading")}</Loader>
        </Dimmer>
    );
  };


  const componentsRenderer = ({rowData}: any) => {

    const componentIds = rowData.components;
    const componentCodes = componentIds.map((compId: LabTestComponentDto) => {
      const component = labTestComponents[compId.id];
      return component ? component.code : "";
    });

    return (
        <>
          <div>{componentCodes.join(", ")}</div>
        </>
    )
  }

  const actionCellRenderer = ({rowData}: any) => {

    return (
        <>
          <EditActionButton
              hoverMessage={getMessage("msgEdit")}
              onConfirm={() => openAddEditModal(rowData.id)}
          />
          <DeleteActionButton
              hoverMessage={getMessage("msgDelete")}
              popupMessage={getMessage("labTestPanel.confirmDelete")}
              onConfirm={() => labTestPanelDelete(rowData.id)}/>
        </>
    )
  }

  const addButtonRenderer = (): React.ReactNode => {

    return (
        <PxGrid>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
            </Grid.Column>
            <Grid.Column width={4} verticalAlign={"top"}>
            </Grid.Column>
            <Grid.Column width={9}>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => setAddEditModalOpen(true)}/>
            </Grid.Column>
          </Grid.Row>
        </PxGrid>
    )
  }

  const labTestPanelRowGetter = ({index}: any) => {

    if (labTestPanels) {
      Object.assign(labTestPanels[index], {index: index + 1});
      return labTestPanels[index];
    }
  }

  const labTestPanelRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role={"row"}
            style={style}
            onDoubleClick={() => openAddEditModal(labTestPanels && labTestPanels[index].id)}
        >
          {columns}
        </div>
    )
  }

  const renderLabTestPanel = (): JSX.Element => {

    return (
        <VirtualizedTable
            rowCount={labTestPanels.length}
            rowGetter={labTestPanelRowGetter}
            rowRenderer={labTestPanelRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "id"
              },
              {
                width: 300,
                label: (getMessage("labTestPanel.code")),
                dataKey: "code"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("labTestPanel.name")),
                dataKey: "name"
              },
              {
                width: 300,
                flexGrow: 2,
                label: (getMessage("labTestPanel.components")),
                dataKey: "components",
                cellRenderer: (componentsRenderer)
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (actionCellRenderer)
              }
            ]}
        />
    )
  }

  const renderLabTestComponentModal = (): React.ReactNode => {
    return (
        <Modal
            open={addEditModalOpen}
            onClose={hideModal}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("header.navigation.labTestPanel")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertLabTestPanelForm
                labTestPanelId={labTestPanelId}
                labTestPanel={labTestPanel}
                onCancel={hideModal}
                onSave={onSaveUpsertForm}
            />
          </FormModalContentContainer>
        </Modal>
    )
  }

  if (!labTestPanelsLoaded) {
    return <></>;
  }

  return (
      <PraxStyledSegment>
        <div>
          {addButtonRenderer()}
        </div>
        <StyledViewTableRow>
          {/*{!labTestComponentsLoaded ? renderLoader() : renderLabTestComponents()}*/}
          {renderLabTestPanel()}
        </StyledViewTableRow>

        {renderLabTestComponentModal()}
      </PraxStyledSegment>
  )
}

export default withMessages(LabTestPanelView);
